<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import Pagination from '$lib/Pagination.svelte';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faChevronUp,
    faChevronDown,
    faRotate,
    faCopy,
    faCheck,
    faCheckSquare,
  } from '@fortawesome/pro-light-svg-icons';
  import Footer from '$lib/Footer.svelte';
  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';

  let stats = [];
  let limit = 1000;
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let isLoading = true;
  let selectedType = 'ENROLLED';
  let selectedYear = 'ALL';
  let showOnlyDelayed = false;
  let threshold = 99999;
  let copySuccess = false;
  let statsTable;
  let tableContainer;
  let showToast = false;
  let toastMessage = '';

  let currentDateTime = new Date().toLocaleString();

  onMount(async () => {
    //selectedStudy.set(null);
    selectedSite.set(null);
    selectedParticipant.set(null);

    if ($selectedStudy) {
      fetchStats(1);
    }
  });

  function handleParticipantClick(site, person) {
    selectedSite.set(site);
    selectedParticipant.set(person);
    goto(`/person`);
  }

  function handleRefresh() {
    fetchStats(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchStats(newPage);
    }
  }

  function showToastMessage(message, duration = 3000) {
    toastMessage = message;
    showToast = true;
    setTimeout(() => {
      showToast = false;
    }, duration);
  }

  async function copyTableToClipboard() {
    try {
      if (!tableContainer) return;

      // Create a range to select the container with title and table
      const range = document.createRange();
      range.selectNode(tableContainer);

      // Clear any existing selections
      window.getSelection().removeAllRanges();

      // Add our new range
      window.getSelection().addRange(range);

      // Execute the copy command
      document.execCommand('copy');

      // Deselect the container
      window.getSelection().removeAllRanges();

      copySuccess = true;
      showToastMessage('Table copied to clipboard!');
      setTimeout(() => {
        copySuccess = false;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy table to clipboard:', error);
      showToastMessage('Failed to copy table. Please try again.', 4000);
    }
  }

  async function fetchStats(page = 1) {
    try {
      isLoading = true; // Set loading to true before starting the fetch

      let subjectStatuses;
      switch (selectedType) {
        case 'ACTIVE':
          subjectStatuses = ['Screened/Enrolled'];
          threshold = 2100;
          break;
        case 'INACTIVE':
          subjectStatuses = ['Screen Failed', 'Discontinued', 'Completed'];
          threshold = 999999;
          break;
        case 'COMPLETED':
          subjectStatuses = ['Completed'];
          threshold = 999999;
          break;
        case 'DISCONTINUED':
          subjectStatuses = ['Discontinued'];
          threshold = 999999;
          break;
        case 'ENROLLED':
          subjectStatuses = ['Screened/Enrolled', 'Enrolled'];
          threshold = 999999;
          break;
        case 'FAILED':
          subjectStatuses = ['Screen Failed'];
          threshold = 999999;
          break;
        default:
          subjectStatuses = [
            'Completed',
            'Discontinued',
            'Initiated',
            'Consented',
            'Screened',
            'Screened/Enrolled',
            'Enrolled',
            'Screen Failed',
          ];
          threshold = 999999;
      }

      const subjectStatusesJson = JSON.stringify(subjectStatuses);
      let apiUrl = `${API_BASE_URL}/stats?page=${page}&pageSize=${limit}&threshold=${threshold}&studyGUID=${$selectedStudy._id}&subjectStatuses=${encodeURIComponent(subjectStatusesJson)}`;

      // Add year parameter if a specific year is selected
      if (selectedYear === 'DAYS_14') {
        apiUrl += `&days=14`;
      } else if (selectedYear === 'DAYS_28') {
        apiUrl += `&days=28`;
      } else if (selectedYear === 'DAYS_56') {
        apiUrl += `&days=56`;
      } else if (selectedYear !== 'ALL') {
        apiUrl += `&year=${selectedYear}`;
      }

      const res = await fetch(apiUrl);

      if (!res.ok) {
        throw new Error('Failed to fetch stats');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      stats = responseData.data;

      //console.info('Stats fetched:', stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      isLoading = false; // Set loading to false after fetch is complete
    }
  }

  // Function to format Latest as local date string
  function formatLocalDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  // Function to calculate time difference in days and hours
  function calculateTimeDifference(dateString) {
    const now = new Date();
    const pastDate = new Date(dateString);
    const diffMs = now - pastDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    // Return empty string if the difference is less than an hour
    if (diffDays === 0 && diffHours === 0) {
      return '';
    }

    // Return only the days part if the difference is a day or more
    if (diffDays >= 1) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    }

    // Return the hours part if the difference is less than a day
    return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  }
</script>

<div class="flex justify-between ph-2 pb-0 me-1">
  <h2 class="text-l mb-4 mx-4 text-sky-800 dark:text-white">Subject CGM Status</h2>
  <div class="text-m mb-0 text-sky-800 dark:text-white">
    Status
    <select
      bind:value={selectedType}
      class="border text-xs p-2 mx-2 mb-1 w-36 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
    >
      <option value="ALL">All</option>
      <option value="ACTIVE">Active (2100)</option>
      <option value="INACTIVE">Inactive</option>
      <option value="COMPLETED">Completed</option>
      <option value="DISCONTINUED">Discontinued</option>
      <option value="ENROLLED">Enrolled</option>
      <option value="FAILED">Screen Failed</option>
    </select>
    Range
    <select
      bind:value={selectedYear}
      class="border text-xs p-2 mx-2 mb-1 w-24 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
      on:change={() => fetchStats(1)}
    >
      <option value="ALL" selected>All</option>
      <option value="2025">2025</option>
      <option value="DAYS_14">14 Days</option>
      <option value="DAYS_28">28 Days</option>
      <option value="DAYS_56">56 Days</option>
    </select>
    Filter
    <select
      bind:value={showOnlyDelayed}
      class="border text-xs p-2 mx-2 mb-1 w-36 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
      on:change={() => fetchStats(1)}
    >
      <option value={false}>All</option>
      <option value={true}>Behind (>1 day)</option>
    </select>
    {resultCount} Items
    <button on:click={() => handleRefresh()} class="pagination mx-1 p-0">
      <FontAwesomeIcon icon={faRotate} class="text-sky-800 dark:text-white" />
    </button>
    <button on:click={() => copyTableToClipboard()} class="pagination mx-1 p-0">
      <FontAwesomeIcon
        icon={copySuccess ? faCheckSquare : faCopy}
        class="text-sky-800 dark:text-white"
      />
    </button>
  </div>
</div>

<!-- Toast notification -->
{#if showToast}
  <div class="toast-container">
    <div class="toast-message">
      <span>{toastMessage}</span>
    </div>
  </div>
{/if}

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
      preparing subject status report
    </p>
  </div>
{:else}
  <div bind:this={tableContainer} class="px-1 pt-0">
    <h2 class="text-base font-semibold mb-3 text-sky-800 dark:text-white">
      {$selectedStudy
        ? 'Protocol: ' + $selectedStudy.protocolNumber + ' - Subject CGM Status'
        : 'Subject CGM Status'} - {currentDateTime}
    </h2>
    <table bind:this={statsTable} class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Site</th>
          <th class="px-4 py-2 text-left bold">Subject</th>
          <th class="px-4 py-2 text-left bold">Latest CGM (Browser)</th>
          <th class="px-4 py-2 text-left bold">Behind</th>
          <th class="px-4 py-2 text-left bold">Readings</th>
          <th class="px-4 py-2 text-left bold">Phase</th>
          <th class="px-4 py-2 text-left bold">Subj Status</th>
          <!-- <th class="px-4 py-2 text-left bold">Status Date (Browser)</th> -->
          <th class="px-4 py-2 text-left bold">Phone/App</th>
        </tr>
      </thead>
      <tbody>
        {#each stats as stat, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{stat.SiteId}</td>
            <td
              class="px-4 py-2 cursor-pointer"
              on:click={() => handleParticipantClick(stat.Site, stat.Subject)}
            >
              {stat.SubjectId}
            </td>
            <td class="px-4 py-2">{formatLocalDate(stat.Latest)}</td>
            <td
              class="px-4 py-2 {stat.Count > 0 &&
              calculateTimeDifference(stat.Latest).includes('day')
                ? 'text-red-700 dark:text-red-500'
                : ''}"
            >
              {stat.Count > 0 ? calculateTimeDifference(stat.Latest) : ''}
            </td>
            <td class="px-4 py-2">{stat.Count}</td>
            <td class="px-4 py-2">{stat.CurrentVisit ?? '(Completed)'}</td>
            <td class="px-4 py-2">{stat.Status}</td>
            <!-- <td class="px-4 py-2">{stat.StatusDate}</td> -->
            <td class="px-4 py-2"
              >{stat.AppOS === 'iOS'
                ? stat.AppInfo
                  ? stat.AppInfo
                  : 'iOS'
                : `${stat.AppOS} ${stat.AppVersion.replace('SUMMA Mobile', '/')}`}</td
            >
          </tr>
        {/each}
      </tbody>
    </table>

    <!-- New table for delayed subjects -->
    {#if showOnlyDelayed && stats.some((stat) => stat.Count > 0 && calculateTimeDifference(stat.Latest).includes('day'))}
      <div class="mt-8">
        <h3 class="text-l mb-4 mx-4 text-sky-800 dark:text-white">Action Suggestions</h3>
        <table class="min-w-full border-collapse table-auto">
          <thead>
            <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
              <th class="px-4 py-2 text-left bold w-32">Subject</th>
              <th class="px-4 py-2 text-left bold w-32">Behind</th>
              <th class="px-4 py-2 text-left bold">Suggestion</th>
            </tr>
          </thead>
          <tbody>
            {#each stats.filter((stat) => stat.Count > 0 && calculateTimeDifference(stat.Latest).includes('day')) as stat, index}
              <tr
                class="text-sm hover:bg-stone-300 dark:hover:bg-gray-900/30 text-gray-800 dark:text-slate-300 {index %
                  2 ===
                0
                  ? 'bg-white dark:bg-gray-700'
                  : 'bg-stone-100 dark:bg-gray-750'}"
              >
                <td
                  class="px-4 py-2 cursor-pointer"
                  on:click={() => handleParticipantClick(stat.Site, stat.Subject)}
                >
                  {stat.SubjectId}
                </td>
                <td class="px-4 py-2 text-red-700 dark:text-red-500">
                  {calculateTimeDifference(stat.Latest)}
                </td>
                <td class="px-4 py-2">tbd</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
{/if}

<!-- Add Export Buttons -->
{#if totalPages > 1}
  <Footer>
    <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  </Footer>
{/if}

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  .toast-message {
    background-color: #4caf50;
    color: white;
    padding: 12px 20px;
    border-radius: 4px;
    margin-bottom: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    animation:
      slideIn 0.3s,
      fadeOut 0.5s 2.5s;
    max-width: 300px;
    font-size: 14px;
  }

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
</style>
